class CartAddOns extends HTMLElement {
  connectedCallback() {
    // Get the cart add-ons
    this.cartAddOns = this.querySelectorAll('[data-cart-addon]');

    // Listen for cart updates to update the add-on products
    this.unsubscribeCartUpdated = subscribe('cart:updated', ({ sections }) => {
      this.update(sections);
    });

    // Listen for toggle of add-on products to add/remove from cart
    this.cartAddOns.forEach((cartAddOn) => {
      cartAddOn.querySelectorAll('[data-cart-addon-variant-id]').forEach((checkbox) => {
        checkbox.addEventListener('change', (event) => {
          event.preventDefault();
          this.toggleAddToCart(cartAddOn);
        });
      });
    });

    // Initialize, check if the add-on products are valid
    this.initialize();
  }

  /**
   * Unsubscribe from the cart updated event
   */
  disconnectedCallback() {
    this.unsubscribeCartUpdated();
    this.abortController = null;
  }

  /**
   * Update the add-on products when the cart is updated
   * @param {Object} sections - The sections object
   */
  update(sections) {
    const html = new DOMParser().parseFromString(sections['fetch-line-items'], 'text/html');
    const cartAddOns = html.querySelector('#cart-add-ons');
    if (cartAddOns.classList.contains('hidden')) {
      this.closest('#cart-add-ons').classList.add('hidden');
    }
    this.closest('#cart-add-ons').innerHTML = cartAddOns.innerHTML;
  }

  /**
   * Initialize, check if the add-on products in the cart are valid
   */
  async initialize() {
    try {
      // Get the add-on items that may be in the cart with there correct quantity
      const addOnState = [...this.cartAddOns].map((addOn) => ({
        quantity: parseInt(addOn.querySelector('[data-cart-addon-quantity]').value) || 1,
        id: parseInt(addOn.querySelector('[data-cart-addon-variant-id]').value),
        checked: addOn.querySelector('[data-cart-addon-variant-id]').checked,
      }));

      // Get the current cart add on items
      const cart = await this.getCart();
      const cartAddOns = cart.items.filter((item) => item.properties['_cart-add-on'] === true);

      // Formdata to update the cart
      const formData = {
        updates: {},
      };

      // Loop threw the add on state
      cartAddOns.forEach((addOn) => {
        const itemState = addOnState.find((addOnState) => addOnState.id === addOn.id);
        // Remove line item if it's not in the state
        if (!itemState) {
          formData.updates[addOn.id] = 0;
        }
        // Update the line item if the quantity is different
        if (itemState && itemState.quantity !== addOn.quantity) {
          formData.updates[addOn.id] = itemState.quantity;
        }
      });

      // Update the cart when there are updates
      if (Object.keys(formData.updates).length > 0) {
        await this.updateCartLineItems(formData);
        publish('cart-add-ons:updated');
      }
    } catch (error) {
      this.onError(error);
      publish('cart-add-ons:error');
    }
  }

  /**
   * Toggle the add-on product to add/remove from cart
   * @param {HTMLElement} cartAddOn - The cart add-on element
   */
  async toggleAddToCart(cartAddOn) {
    if (!cartAddOn) return;

    // Abort controller
    if (this.abortController) return;
    this.abortController = new AbortController();

    // Get the variant ID and quantity inputs
    const variantIdInput = cartAddOn.querySelector('[data-cart-addon-variant-id]');
    const quantityInput = cartAddOn.querySelector('[data-cart-addon-quantity]');

    // Get the add state, variant ID and quantity
    const add = variantIdInput.checked;
    const variantId = parseInt(variantIdInput.value);
    const quantity = parseInt(quantityInput.value) || 1;

    // Publish the event to update the cart add-ons
    publish('cart-add-ons:updating', {
      add,
      quantity,
      variantId,
    });

    try {
      // Get the current cart
      const cart = await this.getCart();

      // Is the current add-on item in the cart?
      const hasAddOnItemInCart = cart.items.filter(
        (item) => item.id === variantId && item.properties['_cart-add-on'] === true
      );

      // Get the current quantity of the add-on item in the cart
      const currentAddOnQuantity =
        hasAddOnItemInCart.length > 0 ? hasAddOnItemInCart[0].quantity : 0;

      // Add item: If not has add-on item in cart and add is checked
      if (hasAddOnItemInCart.length === 0 && add) {
        const formData = {
          items: [
            {
              id: variantId,
              quantity,
              properties: {
                '_cart-add-on': true,
              },
            },
          ],
        };
        await this.addToCart(formData);
      }

      // Update quantity: If has add-on item in cart and quantity is different
      else if (hasAddOnItemInCart.length > 0 && quantity !== currentAddOnQuantity && add) {
        const formData = {
          updates: {
            [hasAddOnItemInCart[0].key]: quantity,
          },
        };
        await this.updateCartLineItems(formData);
      }

      // Remove item: If has add-on item in cart and add is not checked
      else if (hasAddOnItemInCart.length > 0 && !add) {
        const formData = {
          updates: {
            [hasAddOnItemInCart[0].key]: 0,
          },
        };
        await this.updateCartLineItems(formData);
      } else {
        return;
      }

      // Publish the event to update the cart add-ons
      publish('cart-add-ons:updated', {
        add,
        quantity,
        variantId,
      });
    } catch (error) {
      this.onError(error);
      publish('cart-add-ons:error', {
        add,
        quantity,
        variantId,
      });
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Get the current cart
   */
  async getCart() {
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to retrieve cart');
      }
      return data;
    } catch (error) {
      throw new Error(error || 'Failed to retrieve cart');
    }
  }

  /**
   * Add to cart
   * @param {object} formData - The form data
   * @returns {Object}
   */
  async addToCart(formData) {
    try {
      if (!formData) {
        throw new Error('Form data is missing');
      }
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to add to cart');
      }
      return data;
    } catch (error) {
      throw new Error(error || 'Failed to add to cart');
    }
  }

  /**
   * Update the quantity of the add-on items in the cart
   * @param {object} formData - The form data
   * @returns {Object}
   */
  async updateCartLineItems(formData) {
    try {
      if (!formData) {
        throw new Error('Form data is missing');
      }
      const response = await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to remove from cart');
      }
      return data;
    } catch (error) {
      throw new Error(error || 'Failed to remove from cart');
    }
  }

  /**
   * Handle form error
   * @param {Error} error - The error object
   */
  onError({ message }) {
    console.error(message);
    window.toast.error(message);
  }
}

customElements.define('cart-add-ons', CartAddOns);
